import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  Autocomplete,
  Popper,
  Paper,
  IconButton,
  Typography,
  Stack,
} from '@mui/material';
import { AU_STATES, CA_STATES, US_STATES } from '@features/WotcSurvey/constants';
import { Controller, useForm } from 'react-hook-form';
import { white } from '@styles/colors';
import { getTimeZones } from '@vvo/tzdb';
import { Close } from '@mui/icons-material';
import { EditIcon } from '../../../vectors/index';
import { CompanyData } from '../types';
import { useUserCountry } from '@context/UserCountryContext';
import tzLookup from 'tz-lookup';

interface PlacePrediction {
  description?: string;
  place_id?: string;
  structured_formatting: {
    main_text: string;
  };
}
interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

interface PlaceDetails {
  geometry: {
    location: {
      lat: () => number;
      lng: () => number;
    };
  };
  formatted_phone_number?: string;
  utc_offset_minutes?: string;
  formatted_address?: string;
  vicinity?: string;
  address_components: AddressComponent[];
  website?: string;
  name?: string;
  utc_offset?: number;
}

interface PlaceDetailsState {
  s_lat: number;
  s_lon: number;
  phone: string;
  s_city: string;
  s_state: string;
  postal_code: string;
  s_zip: string;
  website: string;
  timezone: string;
  name: string;
}
interface EditCompanyDialogProps {
  open: boolean;
  handleClose: () => void;
  company: CompanyData;
  onSave: (updatedCompany: CompanyData) => void;
}

const EditCompanyDialog: React.FC<EditCompanyDialogProps> = ({
  open,
  handleClose,
  company,
  onSave,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<CompanyData>({
    defaultValues: company,
    mode: 'onBlur',
    reValidateMode: 'onChange',
  });
  const TIMEZONES = getTimeZones();

  const [valueOption, setValueOption] = useState<Object | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [options, setOptions] = useState<PlacePrediction[] | []>([]);
  const { userCountry } = useUserCountry();
  const states = (userCountry === 'au') ? AU_STATES : (userCountry === 'ca') ? CA_STATES : US_STATES;

  const fetchLocations = async (query: string): Promise<void> => {
    const service = new (
      window as any
    ).google.maps.places.AutocompleteService();
    service.getPlacePredictions(
      { input: query, componentRestrictions: { country: userCountry } },
      (predictions: PlacePrediction[], status: string) => {
        if (
          status === (window as any).google.maps.places.PlacesServiceStatus.OK
        ) {
          setOptions(predictions);
        } else {
          console.error('Error fetching locations:', status);
        }
      },
    );
  };

  const fetchPlaceDetails = async (placeId: string): Promise<void> => {
    const service = new (window as any).google.maps.places.PlacesService(
      document.createElement('div'),
    );

    service.getDetails({ placeId }, (details: PlaceDetails, status: string) => {
      if (
        status === (window as any).google.maps.places.PlacesServiceStatus.OK
      ) {
        const timezone = details.utc_offset;
        const selectedTimezones = TIMEZONES.filter(
          (t) =>
            t.currentTimeOffsetInMinutes === timezone &&
            t.countryCode === (userCountry === 'au' ? 'AU' : userCountry === 'ca' ? 'CA' : 'US'),
        );
        const zone = selectedTimezones
          ? selectedTimezones.length > 1
            ? selectedTimezones[1]
            : selectedTimezones[0]
          : null;
        const addressComponents = details.address_components;
        const tz = tzLookup(details.geometry.location.lat(), details.geometry.location.lng());
        const placeDetails: PlaceDetailsState = {
          name: details.name || '',
          s_lat: details.geometry.location.lat(),
          s_lon: details.geometry.location.lng(),
          phone: details.formatted_phone_number || '',
          s_city:
            addressComponents.find((comp) => comp.types.includes('locality'))
              ?.long_name || '',
          s_state:
            addressComponents.find((comp) =>
              comp.types.includes('administrative_area_level_1'),
            )?.short_name || '',
          postal_code:
            addressComponents.find((comp) => comp.types.includes('postal_code'))
              ?.long_name || '',
          s_zip:
            addressComponents.find((comp) => comp.types.includes('postal_code'))
              ?.long_name || '',
          website: details.website || '',
          timezone: tz ? tz : '',
        };

        setOptions(
          options.map((item) =>
            item.place_id === placeId ? { ...item, name: details.name } : item,
          ),
        );
        setValueOption({
          description: details.name,
          structured_formatting: { main_text: details.name },
          place_id: placeId,
        });
        setValue('lat', placeDetails.s_lat ?? '');
        setValue('lng', placeDetails.s_lon ?? '');
        setValue('zip_code', placeDetails.s_zip ?? '');
        setValue('company_name', placeDetails.name);
        setValue('state', placeDetails?.s_state ?? '');
        setValue('city', placeDetails?.s_city ?? '');
        setValue('phone', placeDetails?.phone ?? '');
        setValue('timezone', placeDetails?.timezone ?? '');
        setValue('website', placeDetails?.website ?? '');
        if (details?.name) {
          setInputValue(details.name);
        }
      } else {
        console.error('Error fetching place details:', status);
      }
    });
  };

  useEffect(() => {
    if (!company) return;

    const fieldsToUpdate: (keyof CompanyData)[] = [
      'email',
      'company_name',
      'lat',
      'lng',
      'phone',
      'timezone',
      'zip_code',
      'state',
      'street_address',
    ];

    fieldsToUpdate.forEach((field) => {
      if (company[field as keyof CompanyData]) {
        setValue(field, company[field]);
      }
    });

    if (company.company_name) {
      const companyInfo = {
        description: company.company_name,
        structured_formatting: { main_text: company.company_name },
      };
      setValueOption(companyInfo);
      setInputValue(company.company_name);
      setOptions([companyInfo]);
    }
  }, [company]);

  const onSubmit = (data: CompanyData) => {
    const lat = watch('lat');
    const phone = watch('phone');
    const lng = watch('lng');
    const timezone = watch('timezone');
    const website = watch('website');
    const state = watch('state');
    const city = watch('city');
    const street_address = watch('street_address');
    const company_name = watch('company_name');
    const email = watch('email');
    onSave({
      ...data,
      lat,
      lng,
      phone,
      timezone,
      website,
      state,
      city,
      email,
      street_address,
      company_name,
      status: lat && lng ? 'Valid' : 'Invalid Address',
    });
  };
  const handleCloseModal = () => {
    reset();
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleCloseModal}
        >
          <Close />
        </IconButton>
        <br />
        <EditIcon />
        <br />
        <Typography variant="h4">Edit Company</Typography>
      </DialogTitle>
      <DialogContent sx={{ marginTop: 2 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl
            sx={{ margin: '10px 0' }}
            size="small"
            variant="outlined"
            fullWidth
          >
            <Controller
              name="email"
              control={control}
              rules={{ required: 'Email is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  sx={{ margin: '10px 0' }}
                  margin="dense"
                  label="Email"
                  fullWidth
                  size="small"
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              )}
            />
            <Autocomplete
              fullWidth
              size="small"
              inputValue={inputValue}
              onChange={(event, value: any) => {
                if (value) {
                  fetchPlaceDetails(value.place_id);
                }
              }}
              value={valueOption}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue ? newInputValue : '');
                fetchLocations(newInputValue);
              }}
              freeSolo
              filterOptions={(x) => x}
              //@ts-ignore
              options={options}
              getOptionLabel={(option: any) => option?.description}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Company Name"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                    },
                    background: white,
                  }}
                  helperText={errors?.company_name?.message ?? ''}
                  error={Boolean(errors.company_name)}
                />
              )}
              PopperComponent={(props) => (
                <Popper {...props} sx={{ zIndex: 1300 }}>
                  {props.children}
                </Popper>
              )}
              PaperComponent={(props) => (
                <Paper
                  {...props}
                  sx={{
                    '& .MuiAutocomplete-noOptions': {
                      fontSize: '14px',
                      padding: '10px',
                    },
                    '& .MuiAutocomplete-option': {
                      fontSize: '14px',
                      padding: '5px',
                    },
                    borderRadius: 5,
                    backgroundColor: '#f9f9f9',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  {props.children}
                </Paper>
              )}
            />
          </FormControl>
          <Controller
            name="street_address"
            control={control}
            rules={{ required: 'Street Address is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                margin="dense"
                label="Street Address"
                fullWidth
                size="small"
                error={!!errors.street_address}
                helperText={errors.street_address?.message}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Controller
                name="city"
                control={control}
                rules={{ required: 'City is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="dense"
                    label="City"
                    size="small"
                    fullWidth
                    error={!!errors.city}
                    helperText={errors.city?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="state"
                control={control}
                rules={{ required: 'State is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="dense"
                    select
                    label="State"
                    size="small"
                    variant="outlined"
                    fullWidth
                    error={!!errors.state}
                    helperText={errors.state?.message}
                  >
                    {states.map((state) => (
                      <MenuItem key={state.code} value={state.code}>
                        {state.code}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Controller
                name="zip_code"
                control={control}
                rules={{ required: 'Zip Code is required' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    margin="dense"
                    label="Zip Code"
                    size="small"
                    fullWidth
                    error={!!errors.zip_code}
                    helperText={errors.zip_code?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Stack mt={3} mb={2} spacing={2}>
            <Button
              variant="outlined"
              sx={{ width: '100%' }}
              onClick={handleClose}
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              sx={{ width: '100%' }}
              type="submit"
              color="primary"
              variant="contained"
            >
              Save
            </Button>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditCompanyDialog;
